import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../../../../components/common/Button";
import { ProfileFields } from "../ProfileFields";
import { ContactFields } from "../ContactFields";
import { PasswordFields } from "../PasswordFields";
import { EmailVerifiedCard } from "../InfoCards";

type User = {
    name?: string;
    phone?: string | number | null;
    emailVerified?: string | null;
    subscription?: {
        description: string;
        expiresAt: string | null;
    } | null;
};

type Contacts = {
    telegram?: string | null;
    whatsapp?: string | null;
    instagram?: string | null;
};

export type SavePayload = {
    name?: string | null;
    phone?: string | number | null;
    telegram?: string | null;
    whatsapp?: string | null;
    instagram?: string | null;
    password?: string;
    password_current?: string;
};

type Props = {
    user: User;
    contacts: Contacts;
    onSave: (payload: SavePayload) => void;

    // флаги из стора
    saving?: boolean;
    saveError?: boolean;
    saveErrorMessage?: string | null;
    saveSuccess?: boolean;
};

export const PersonalDataForm: React.FC<Props> = ({
                                                      user,
                                                      contacts,
                                                      onSave,
                                                      saving = false,
                                                      saveError = false,
                                                      saveErrorMessage = null,
                                                      saveSuccess = false,
                                                  }) => {
    // профиль
    const [name, setName] = useState(user?.name || "");
    const [phone, setPhone] = useState(String(user?.phone || ""));

    // контакты
    const [telegram, setTelegram] = useState(String(contacts?.telegram || ""));
    const [whatsapp, setWhatsapp] = useState(String(contacts?.whatsapp || ""));
    const [instagram, setInstagram] = useState(String(contacts?.instagram || ""));

    // пароли
    const [passwordCurrent, setPasswordCurrent] = useState("");
    const [password, setPassword] = useState("");

    // ---- синхронизация с пропсами ----
    useEffect(() => {
        setName(user?.name || "");
        setPhone(String(user?.phone || ""));
    }, [user?.name, user?.phone]);

    useEffect(() => setTelegram(String(contacts?.telegram || "")), [contacts?.telegram]);
    useEffect(() => setWhatsapp(String(contacts?.whatsapp || "")), [contacts?.whatsapp]);
    useEffect(() => setInstagram(String(contacts?.instagram || "")), [contacts?.instagram]);

    // ---- helpers ----
    const trim = (v: string) => v.trim();
    const normalizePhone = (v: string) => trim(String(v));
    const normalizeWhatsapp = (v: string) => trim(v.replace(/[^\d+]/g, "")); // цифры и '+'

    const norm = {
        name: (v: string) => trim(v),
        phone: normalizePhone,
        telegram: (v: string) => trim(v),
        whatsapp: normalizeWhatsapp,
        instagram: (v: string) => trim(v),
    };

    // исходные значения (нормализованные)
    const initial = useMemo(
        () => ({
            name: norm.name(user?.name || ""),
            phone: norm.phone(String(user?.phone || "")),
            telegram: norm.telegram(String(contacts?.telegram || "")),
            whatsapp: norm.whatsapp(String(contacts?.whatsapp || "")),
            instagram: norm.instagram(String(contacts?.instagram || "")),
        }),
        [user?.name, user?.phone, contacts?.telegram, contacts?.whatsapp, contacts?.instagram],
    );

    // текущие значения (нормализованные)
    const current = {
        name: norm.name(name),
        phone: norm.phone(phone),
        telegram: norm.telegram(telegram),
        whatsapp: norm.whatsapp(whatsapp),
        instagram: norm.instagram(instagram),
    };

    // есть ли изменения (учитываем и поля пароля)
    const isChanged = useMemo(() => {
        const baseChanged =
            current.name !== initial.name ||
            current.phone !== initial.phone ||
            current.telegram !== initial.telegram ||
            current.whatsapp !== initial.whatsapp ||
            current.instagram !== initial.instagram;
        const passwordTouched = Boolean(password) || Boolean(passwordCurrent);
        return baseChanged || passwordTouched;
    }, [current, initial, password, passwordCurrent]);

    // правило формирования дельты
    const diffField = (curr: string, init: string): string | null | undefined => {
        if (curr === init) return undefined;
        if (curr === "") return null;
        return curr;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isChanged) return;

        const payload: SavePayload = {};

        const dName = diffField(current.name, initial.name);
        const dPhone = diffField(current.phone, initial.phone);
        const dTg = diffField(current.telegram, initial.telegram);
        const dWa = diffField(current.whatsapp, initial.whatsapp);
        const dIg = diffField(current.instagram, initial.instagram);

        if (dName !== undefined) payload.name = dName;
        if (dPhone !== undefined) payload.phone = dPhone;
        if (dTg !== undefined) payload.telegram = dTg;
        if (dWa !== undefined) payload.whatsapp = dWa;
        if (dIg !== undefined) payload.instagram = dIg;

        // пароли отправляем ТОЛЬКО если заполнены оба
        if (trim(password) && trim(passwordCurrent)) {
            payload.password = password;
            payload.password_current = passwordCurrent;
        }

        if (Object.keys(payload).length === 0) return;

        onSave(payload);
    };

    return (
        <form className="bg-white p-6 pt-0 flex flex-col gap-6" onSubmit={handleSubmit}>
            <ProfileFields
                name={name}
                phone={phone}
                onChange={(f, v) => {
                    if (f === "name") setName(v);
                    if (f === "phone") setPhone(v);
                }}
            />

            <div className="border-t pt-4">
                <div className="text-sm text-gray-500 mb-2">Контакты</div>
                <ContactFields
                    telegram={telegram}
                    whatsapp={whatsapp}
                    instagram={instagram}
                    onChange={(f, v) => {
                        if (f === "telegram") setTelegram(v);
                        if (f === "whatsapp") setWhatsapp(v);
                        if (f === "instagram") setInstagram(v);
                    }}
                />
            </div>

            <div className="border-t pt-4">
                <div className="text-sm text-gray-500 mb-2">Смена пароля</div>
                <PasswordFields
                    passwordCurrent={passwordCurrent}
                    password={password}
                    onChange={(f, v) => {
                        if (f === "password_current") setPasswordCurrent(v);
                        if (f === "password") setPassword(v);
                    }}
                />
            </div>

            {user?.emailVerified && <EmailVerifiedCard dateISO={user.emailVerified} />}

            {!saving && saveSuccess && !saveError && (
                <div className="text-sm text-green-600">Сохранено</div>
            )}
            {!saving && saveError && (
                <div className="text-sm text-red-600">
                    Не удалось сохранить изменения{saveErrorMessage ? `: ${saveErrorMessage}` : "."}
                </div>
            )}

            <div className="pt-4">
                <Button
                    type="submit"
                    text="Сохранить изменения"
                    pending={saving}
                    disabled={!isChanged}
                />
            </div>
        </form>
    );
};
